/**
 * ProfileStatusService - Frontend-only status management
 * 
 * This service provides clean, maintainable status determination using
 * existing Redux store data without requiring API changes.
 */

export class ProfileStatusService {
  /**
   * Get comprehensive profile status information
   * @param {Object} profileData - Combined data from Redux store
   * @returns {Object} Status information with message, color, and flags
   */
  static getStatus(profileData) {
    const {
      // Profile data
      _id,
      status,
      isSubmitted,
      profileUser,
      
      // User data
      accountStatus,
      profileDisabled,
      adminDisabled,
      
      // Additional data
      hasPaid,
      hasSubjects,
      isProfileCompleted
    } = profileData;

    // Handle missing profile
    if (!_id) {
      return {
        status: 'NOT_CREATED',
        message: 'You haven\'t created a profile yet.',
        color: '#da1e28',
        action: 'Create Profile',
        actionable: true
      };
    }

    // Handle admin disabled
    if (adminDisabled) {
      return {
        status: 'ADMIN_DISABLED',
        message: 'Your profile has been disabled by admin and is not visible in search. Please contact support.',
        color: '#da1e28',
        action: 'Contact Support',
        actionable: true
      };
    }

    // Handle user disabled
    if (profileDisabled) {
      return {
        status: 'USER_DISABLED',
        message: 'Your profile is hidden from search. You can enable it from Account Settings.',
        color: '#ba4e00',
        action: 'Enable Profile',
        actionable: true
      };
    }

    // Handle paused/hidden subscription (status === 2 means profile is paused/hidden)
    // This happens when subscription is paused or cancelled
    if (status === 2) {
      if (hasPaid === false) {
        return {
          status: 'PAYMENT_LAPSED',
          message: 'Your payment plan has been cancelled. Your profile is no longer visible to students. Please resubscribe to restore visibility.',
          color: '#da1e28',
          action: 'Resubscribe',
          actionable: true
        };
      }
      return {
        status: 'PAUSED',
        message: 'Your subscription is paused. Your profile is not visible to students. Resume your subscription to make it live again.',
        color: '#ba4e00',
        action: 'Resume Subscription',
        actionable: true
      };
    }

    // Handle cancelled/unpaid subscription (profile was live but payment lapsed)
    if (status === 1 && accountStatus === 1 && hasPaid === false) {
      return {
        status: 'PAYMENT_LAPSED',
        message: 'Your payment plan has been cancelled. Your profile is no longer visible to students. Please resubscribe to restore visibility.',
        color: '#da1e28',
        action: 'Resubscribe',
        actionable: true
      };
    }

    // Handle live profile
    if (status === 1 && accountStatus === 1) {
      return {
        status: 'LIVE',
        message: 'Your profile has been approved by admin and is now live on the website. All required information is completed.',
        color: '#24A148',
        action: null,
        actionable: false
      };
    }

    // Handle pending approval
    if (status === 0 && accountStatus === 0 && isSubmitted) {
      return {
        status: 'PENDING_APPROVAL',
        message: 'You have submitted a profile, but has not been approved by admin.',
        color: '#ba4e00',
        action: 'Wait for Approval',
        actionable: false
      };
    }

    // Handle ready to submit (complete + paid + not submitted)
    // Note: We don't gate on accountStatus here because some stores may
    // still have accountStatus=1 after a mutation while the teacher.status
    // correctly moved to draft (0). The actionable state is determined by
    // teacher.status and submission/payment flags.
    if (isProfileCompleted && hasPaid && status === 0 && !isSubmitted) {
      return {
        status: 'READY_TO_SUBMIT',
        message: 'Your profile is not live yet. Please click the \'Submit Profile\' button to have it approved and made live on the website.',
        color: 'red',
        action: 'Submit Profile',
        actionable: true
      };
    }

    // Handle payment required (only when profile is fully complete including subjects)
    if (isProfileCompleted && hasSubjects && !hasPaid && status === 0) {
      return {
        status: 'PAYMENT_REQUIRED',
        message: 'Your profile is complete but payment is required. Please make payment to submit your profile.',
        color: 'red',
        action: 'Make Payment',
        actionable: true
      };
    }

    // Handle incomplete profile
    if (status === 0) {
      return {
        status: 'DRAFT',
        message: 'Your profile is not live.',
        color: '#da1e28',
        action: 'Complete Profile',
        actionable: true
      };
    }

    // Default fallback: treat as DRAFT (safer than Unknown)
    return {
      status: 'DRAFT',
      message: 'Your profile is not live.',
      color: '#da1e28',
      action: 'Complete Profile',
      actionable: true
    };
  }

  /**
   * Get status color for UI styling
   * @param {string} status - Status key
   * @returns {string} Color code
   */
  static getStatusColor(status) {
    const colors = {
      'NOT_CREATED': '#da1e28',
      'ADMIN_DISABLED': '#da1e28',
      'USER_DISABLED': '#ba4e00',
      'LIVE': '#24A148',
      'PAUSED': '#ba4e00',
      'PENDING_APPROVAL': '#ba4e00',
      'READY_TO_SUBMIT': '#00AB55',
      'PAYMENT_REQUIRED': 'red',
      'PAYMENT_LAPSED': '#da1e28',
      'DRAFT': '#da1e28',
      // No UNKNOWN fallback; default handled by DRAFT
    };
    return colors[status] || '#ba4e00';
  }

  /**
   * Get status message for display
   * @param {string} status - Status key
   * @returns {string} User-friendly message
   */
  static getStatusMessage(status) {
    const messages = {
      'NOT_CREATED': 'Not Created',
      'ADMIN_DISABLED': 'Disabled by admin',
      'USER_DISABLED': 'Hidden',
      'LIVE': 'Live',
      'PAUSED': 'Paused',
      'PENDING_APPROVAL': 'Pending admin review (before going live)',
      'READY_TO_SUBMIT': 'Ready to Submit',
      'PAYMENT_REQUIRED': 'Payment Required',
      'PAYMENT_LAPSED': 'Cancelled',
      'DRAFT': 'Draft'
    };
    // No UNKNOWN: default to a safe generic label
    return messages[status] || 'Draft';
  }

  /**
   * Check if profile is in a state that allows editing
   * @param {string} status - Status key
   * @returns {boolean} Can edit
   */
  static canEdit(status) {
    return ['DRAFT', 'READY_TO_SUBMIT', 'PAYMENT_REQUIRED', 'USER_DISABLED'].includes(status);
  }

  /**
   * Check if profile is visible to students
   * @param {string} status - Status key
   * @returns {boolean} Is visible
   */
  static isVisible(status) {
    return status === 'LIVE';
  }

  /**
   * Check if profile requires admin approval
   * @param {string} status - Status key
   * @returns {boolean} Requires approval
   */
  static requiresApproval(status) {
    return ['PENDING_APPROVAL', 'READY_TO_SUBMIT'].includes(status);
  }
}

export default ProfileStatusService;
